import { useState } from "react";
import { playNow } from "@/audio/piano";

/**
 * Hand Ratio visualisation — faithful to Nathaniel's lesson.
 *
 * In the original transcript the ratio is BIDIRECTIONAL: not only does the
 * right hand pulse N times under one left-hand bass (1:N), the left hand
 * can also pulse N times under one right-hand chord (N:1), and both hands
 * can be active in mixed counts (2:4, 4:2). Both hands always START on
 * beat 1 — Nathaniel says they "collide at the 1 and then go their
 * separate ways".
 *
 * Each row below shows one bar split into 8 sixteenth-note cells. The
 * blue dots are LH bass hits; the orange dots are RH chord stabs. They
 * always overlap on beat 1.
 */

type RatioId = "1:4" | "1:3" | "4:1" | "3:1" | "1:2" | "2:1" | "2:4" | "4:2";

interface RatioRow {
  id: RatioId;
  lhCells: number[];
  rhCells: number[];
  caption: string;
}

const ROWS: RatioRow[] = [
  { id: "1:4", lhCells: [0],          rhCells: [0, 2, 4, 6],   caption: "Left hits 1 · right pulses 4. The pop default." },
  { id: "1:3", lhCells: [0],          rhCells: [0, 2, 5],      caption: "Left hits 1 · right pulses 3. Suits 3/4 waltz." },
  { id: "1:2", lhCells: [0],          rhCells: [0, 4],         caption: "Left hits 1 · right answers on the off-beat." },
  { id: "4:1", lhCells: [0, 2, 4, 6], rhCells: [0],            caption: "Reggae feel — left walks, right stamps once." },
  { id: "3:1", lhCells: [0, 2, 5],    rhCells: [0],            caption: "Walking left, single right chord per bar." },
  { id: "2:1", lhCells: [0, 4],       rhCells: [0],            caption: "Half-time bass · single right chord." },
  { id: "2:4", lhCells: [0, 4],       rhCells: [0, 2, 4, 6],   caption: "Both hands active — fullest texture." },
  { id: "4:2", lhCells: [0, 2, 4, 6], rhCells: [0, 4],         caption: "Driving bass · spaced right chord." },
];

const EM_CHORD = ["B3", "E4", "G4"]; // Nathaniel: B-E-G with thumb-middle-pinky
const EM_BASS = "E2";

export function HandRatioMeter() {
  const [active, setActive] = useState<RatioId | null>(null);
  const [hover, setHover] = useState<RatioId | null>(null);

  const playRow = async (row: RatioRow) => {
    setActive(row.id);
    const interval = 220; // ms per sixteenth ≈ 110 bpm quarter
    const all: { time: number; pitches: string[] }[] = [];
    row.lhCells.forEach((c) => all.push({ time: c, pitches: [EM_BASS] }));
    row.rhCells.forEach((c) => {
      const existing = all.find((e) => e.time === c);
      if (existing) existing.pitches.push(...EM_CHORD);
      else all.push({ time: c, pitches: [...EM_CHORD] });
    });
    all.sort((a, b) => a.time - b.time);
    all.forEach((ev) => {
      setTimeout(() => void playNow(ev.pitches, "16n", 0.7), ev.time * interval + 30);
    });
    const last = Math.max(...all.map((e) => e.time));
    setTimeout(() => setActive(null), last * interval + 700);
  };

  return (
    <div className="my-5 rounded-2xl border border-nsm-border bg-nsm-card/40 p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">
            The ratio dial
          </div>
          <div className="mt-1 font-display text-base text-nsm-ink">
            One bar of Em · 8 sixteenth-note cells · click any row
          </div>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-nsm-dim">
          Both hands collide on beat 1
        </div>
      </div>

      <div className="space-y-2">
        {ROWS.map((row) => {
          const isActive = active === row.id;
          const isHover = hover === row.id;
          const [l, r] = row.id.split(":");
          return (
            <button
              key={row.id}
              onClick={() => playRow(row)}
              onMouseEnter={() => setHover(row.id)}
              onMouseLeave={() => setHover(null)}
              className={`group flex w-full items-stretch gap-2 rounded-xl border p-2 text-left transition ${
                isActive
                  ? "border-amber-500/70 bg-amber-500/10"
                  : isHover
                  ? "border-amber-500/30 bg-zinc-900/60"
                  : "border-nsm-border bg-zinc-900/40"
              }`}
            >
              {/* Ratio numerator/denominator */}
              <div className="flex w-16 flex-shrink-0 flex-col items-center justify-center">
                <div className="font-display text-lg font-bold leading-none text-nsm-ink">
                  <span className="text-blue-400">{l}</span>
                  <span className="mx-1 text-nsm-dim">:</span>
                  <span className="text-amber-400">{r}</span>
                </div>
                <div className="mt-1 text-[9px] uppercase tracking-wider text-nsm-dim">L : R</div>
              </div>

              {/* The 8 cells with stacked LH / RH dots */}
              <div className="flex flex-1 items-center gap-1">
                {Array.from({ length: 8 }).map((_, i) => {
                  const isLH = row.lhCells.includes(i);
                  const isRH = row.rhCells.includes(i);
                  const isBeatOne = i === 0;
                  const isBeat = i % 2 === 0;
                  return (
                    <div
                      key={i}
                      className={`relative flex h-12 flex-1 flex-col items-center justify-center gap-0.5 rounded-md ${
                        isBeatOne ? "border-l-2 border-amber-400/40" : isBeat ? "border-l border-zinc-700" : "border-l border-zinc-800/60"
                      }`}
                      style={{
                        background: isLH && isRH
                          ? "linear-gradient(180deg, rgba(245,158,11,0.16), rgba(59,130,246,0.16))"
                          : isLH
                          ? "rgba(59,130,246,0.10)"
                          : isRH
                          ? "rgba(245,158,11,0.10)"
                          : "transparent",
                      }}
                    >
                      {/* RH dot top */}
                      <span
                        className="h-2 w-2 rounded-full transition"
                        style={{
                          background: isRH ? "#f59e0b" : "transparent",
                          boxShadow: isRH ? "0 0 0 3px rgba(245,158,11,0.22)" : "none",
                        }}
                      />
                      {/* LH dot bottom */}
                      <span
                        className="h-2 w-2 rounded-full transition"
                        style={{
                          background: isLH ? "#3b82f6" : "transparent",
                          boxShadow: isLH ? "0 0 0 3px rgba(59,130,246,0.22)" : "none",
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Caption */}
              <div className="flex w-56 flex-shrink-0 items-center px-2 text-[12px] leading-tight text-nsm-dim">
                {row.caption}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-nsm-dim">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-blue-400" /> Left hand · bass
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Right hand · chord
        </span>
        <span className="ml-auto">
          Practise these ratios <em>on your body first</em> — before you sit down.
        </span>
      </div>
    </div>
  );
}
